// @ts-expect-error TODO(typecheck-unify): TS6133 'React' is declared but its value is never read.
import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Table, Button, Select, InputNumber, Space } from "antd";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { Box, Stack, Tooltip } from "@mui/material";
import { Text } from "@src/lib/ui-react";
import { capitalize } from "lodash";
import { isDefined } from "@clipboard-health/util-ts";

import { fetchHcfDetails } from "./api";
import { Title } from "./title";
import { Toggler } from "./toggler";
import {
  addMultipleOverrides,
  DAYS_OF_WEEK,
  type DayOfWeek,
  findExistingOverrides,
  flattenOverrides,
  type QualificationRateOverride,
  removeOverride,
} from "@src/appV2/FacilityDrawer/Billing/qualificationRateOverrides.utils";
import { ShowError } from "../../../components/error";
import { showErrorToast, showSuccessToast, showWarningToast } from "@src/appV2/lib/Notifications";
import { handleApiError } from "src/utils/errors";
import { Qualification } from "@src/appV2/Admin/Qualifications/types";
import { TIME_SLOTS, TimeSlot } from "@src/appV2/Shifts/types";
import {
  QualificationRatesTableOverrides,
  TimeChargeDifferentialDayOfWeek,
} from "@src/interface/shift";

interface UpdateFacilityResult {
  facility: {
    qualificationRatesTableOverrides: QualificationRatesTableOverrides;
  };
}

interface QualificationRateOverridesProps {
  hcfId: string;
  qualifications: Qualification[];
  updateFacility: (data: {
    facilityId: string;
    qualificationRatesTableOverrides: QualificationRatesTableOverrides;
  }) => Promise<unknown>;
}

function QualificationRateOverrides(props: QualificationRateOverridesProps): JSX.Element {
  const { hcfId, qualifications, updateFacility } = props;
  const [extend, setExtend] = useState<boolean>(false);
  const [overrides, setOverrides] = useState<QualificationRatesTableOverrides>({});
  const [facilityRates, setFacilityRates] = useState<Record<string, number>>({});
  const [error, setError] = useState<Error | undefined>();
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);

  const [newQualifications, setNewQualifications] = useState<string[]>([]);
  const [newDays, setNewDays] = useState<DayOfWeek[]>([]);
  const [newSlots, setNewSlots] = useState<TimeSlot[]>([]);
  const [newRate, setNewRate] = useState<number | undefined>();

  const flatOverrides = useMemo(() => flattenOverrides(overrides), [overrides]);

  const qualificationsWithNonzeroRates = useMemo(
    () =>
      qualifications.filter((qualification) => {
        const rate = facilityRates[qualification.attributes.name];
        return isDefined(rate) && rate > 0;
      }),
    [qualifications, facilityRates]
  );

  useEffect(() => {
    setError(undefined);
    setOverrides({});
    setFacilityRates({});
    setShowAddForm(false);
    setNewQualifications([]);
    setNewDays([]);
    setNewSlots([]);
    setNewRate(undefined);
    setIsFetching(true);

    fetchHcfDetails(hcfId, "qualificationRatesTableOverrides,rates")
      .then((data) => {
        setOverrides(data?.qualificationRatesTableOverrides ?? {});
        setFacilityRates(data?.rates ?? {});
      })
      .catch(setError)
      .finally(() => setIsFetching(false));
  }, [hcfId]);

  const toggle = () => {
    setExtend(!extend);
  };

  const saveOverrides = useCallback(
    async (newOverridesMap: QualificationRatesTableOverrides): Promise<boolean> => {
      setIsLoading(true);
      try {
        const result = (await updateFacility({
          facilityId: hcfId,
          qualificationRatesTableOverrides: newOverridesMap,
        })) as UpdateFacilityResult | undefined;
        // `result` is undefined when saveFacility's retrieveUser refresh
        // failed after a successful profile update. In that case we still
        // treat the save as successful and keep the optimistic-friendly
        // local state; the next full refresh will reconcile.
        if (result?.facility?.qualificationRatesTableOverrides) {
          setOverrides(result.facility.qualificationRatesTableOverrides);
        } else {
          setOverrides(newOverridesMap);
        }
        showSuccessToast("Qualification rate override saved successfully.");
        return true;
      } catch (error) {
        handleApiError(error);
        return false;
      } finally {
        setIsLoading(false);
      }
    },
    [hcfId, updateFacility]
  );

  const handleDelete = useCallback(
    (record: QualificationRateOverride) => {
      const updated = removeOverride(overrides, record);
      void saveOverrides(updated);
    },
    [overrides, saveOverrides]
  );

  const handleAdd = useCallback(() => {
    if (
      newQualifications.length === 0 ||
      newDays.length === 0 ||
      newSlots.length === 0 ||
      typeof newRate !== "number"
    ) {
      showErrorToast("Please fill in all fields.");
      return;
    }

    const existingOverrides = findExistingOverrides({
      overrides,
      qualifications: newQualifications,
      days: newDays,
      slots: newSlots,
    });
    if (existingOverrides.length > 0) {
      const count = existingOverrides.length;
      showWarningToast(
        `${count} override(s) already exist for the selected combinations. They will be updated.`
      );
    }

    const updated = addMultipleOverrides({
      overrides,
      qualifications: newQualifications,
      days: newDays,
      slots: newSlots,
      rate: newRate,
    });

    void saveOverrides(updated).then((success) => {
      if (!success) {
        // Leave the form populated so the user can retry without re-entering values.
        return;
      }
      setNewQualifications([]);
      setNewDays([]);
      setNewSlots([]);
      setNewRate(undefined);
      setShowAddForm(false);
    });
  }, [newQualifications, newDays, newSlots, newRate, overrides, saveOverrides]);

  const qualificationOptions = qualificationsWithNonzeroRates.map((qualification) => ({
    value: qualification.attributes.name,
    label: qualification.attributes.displayLabel,
  }));

  const columns = [
    {
      title: "Qualification",
      dataIndex: "qualification",
      key: "qualification",
      render: (value: string) => {
        const qualification = qualifications.find(
          (qualificationData) => qualificationData.attributes.name === value
        );
        return qualification?.attributes.displayLabel ?? value;
      },
    },
    {
      title: "Day",
      dataIndex: "day",
      key: "day",
      render: (value: TimeChargeDifferentialDayOfWeek) => {
        return capitalize(value);
      },
    },
    {
      title: "Slot",
      dataIndex: "slot",
      key: "slot",
      render: (value: TimeSlot) => value.toUpperCase(),
    },
    {
      title: "Rate",
      dataIndex: "rate",
      key: "rate",
      render: (value: number) => `$${value}/hr`,
    },
    {
      title: "Action",
      key: "action",
      render: (_: unknown, record: QualificationRateOverride) => (
        <Button
          type="text"
          danger
          icon={<DeleteOutlined />}
          onClick={() => handleDelete(record)}
          disabled={isLoading}
        />
      ),
    },
  ];

  return (
    <Box sx={{ marginTop: 3 }}>
      <Title>
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <span>Custom Time Charge per Qualification</span>
          <Tooltip title="Override shift time charges for specific qualifications. These rates are added to the base charge when a shift matches the qualification, day, and time slot.">
            <HelpOutlineIcon fontSize="small" />
          </Tooltip>
          <Toggler extend={extend} onClick={toggle} />
        </Stack>
      </Title>
      <Box sx={{ display: extend ? "block" : "none", marginLeft: 2.5 }}>
        <ShowError error={error} message="Unable to fetch qualification rate overrides" />
        <Text variant="body2" style={{ marginBottom: 16 }}>
          Add custom time charges for specific qualifications. These charges override the default
          shift time charge above for the matching qualification, day, and slot.
        </Text>
        <Table
          columns={columns}
          dataSource={flatOverrides}
          rowKey={(record) => `${record.qualification}-${record.day}-${record.slot}`}
          pagination={false}
          size="small"
          loading={isFetching}
          locale={{ emptyText: "No custom rates configured" }}
        />

        {!showAddForm && (
          <Button
            type="dashed"
            icon={<PlusOutlined />}
            onClick={() => setShowAddForm(true)}
            style={{ marginTop: 16 }}
            disabled={isLoading}
          >
            Add Custom Rate
          </Button>
        )}

        {showAddForm && (
          <Box
            sx={(theme) => ({
              marginTop: 2,
              padding: 2,
              backgroundColor: theme.palette.grey[100],
              borderRadius: 1,
            })}
          >
            <Space direction="vertical" size="middle" style={{ width: "100%" }}>
              <Space wrap>
                <Select
                  mode="multiple"
                  placeholder="Select Qualification(s)"
                  style={{ minWidth: 200 }}
                  options={qualificationOptions}
                  value={newQualifications}
                  onChange={setNewQualifications}
                />
                <Select
                  mode="multiple"
                  placeholder="Select Day(s)"
                  style={{ minWidth: 160 }}
                  options={DAYS_OF_WEEK.map((d) => ({ value: d.value, label: d.label }))}
                  value={newDays}
                  onChange={setNewDays}
                />
                <Select
                  mode="multiple"
                  placeholder="Select Slot(s)"
                  style={{ minWidth: 120 }}
                  options={TIME_SLOTS.map((slot) => ({ value: slot, label: slot.toUpperCase() }))}
                  value={newSlots}
                  onChange={setNewSlots}
                />
                <InputNumber
                  placeholder="Rate"
                  addonBefore="$"
                  addonAfter="/hr"
                  min={0}
                  precision={2}
                  value={newRate}
                  onChange={(value) => setNewRate(value ?? undefined)}
                  style={{ width: 140 }}
                />
              </Space>
              <Space>
                <Button type="primary" onClick={handleAdd} loading={isLoading}>
                  Save
                </Button>
                <Button
                  onClick={() => {
                    setShowAddForm(false);
                    setNewQualifications([]);
                    setNewDays([]);
                    setNewSlots([]);
                    setNewRate(undefined);
                  }}
                  disabled={isLoading}
                >
                  Cancel
                </Button>
              </Space>
            </Space>
          </Box>
        )}
      </Box>
    </Box>
  );
}

export { QualificationRateOverrides };
